import React, { FC } from 'react';
import { Theme, Box, Typography, makeStyles, Container, Grid } from '@material-ui/core';
import Link from 'next/link';
import { privacyPolicyLinks } from 'config/routes/routes';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(5, 0, 20),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(20, 0),
    },
  },
  linksBox: {
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(7, 5, 4),
    position: 'sticky',
    top: 120,
  },
  link: {
    marginBottom: theme.spacing(3),
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  contentBox: {
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(7, 5, 4),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
  },
  sectionBox: {
    marginBottom: theme.spacing(7),
  },
}));

const PrivacyPolicy: FC = () => {
  const classes = useStyles();
  return (
    <Box className={classes.root}>
      <Container fixed>
        <Grid container spacing={5}>
          <Grid item lg={4} xs={12}>
            <Box className={classes.linksBox}>
              {privacyPolicyLinks.map(({ label, route }, index) => (
                <Link href={route} key={index}>
                  <Typography className={classes.link}>{label}</Typography>
                </Link>
              ))}
            </Box>
          </Grid>
          <Grid item lg={8} xs={12}>
            <Box className={classes.contentBox}>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="privacy">
                  Privacy Policy
                </Typography>
                <Typography>
                  At Utilities One, accessible from www.utilitiesone.com, one of our home priorities is the privacy of our
                  visitors. This Privacy Policy document contains information collected and recorded by Utilities One and how we
                  use it.
                </Typography>
                <Box mt={3} />
                <Typography>
                  If you have additional questions or require more information about our Privacy Policy, please contact us.
                </Typography>
                <Box mt={3} />
                <Typography>
                  This Privacy Policy applies only to our online activities and is valid for website visitors regarding the
                  information they share and collect in Utilities One. This policy does not apply to any information collected
                  offline or via channels other than this website.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="consent">
                  Consent
                </Typography>
                <Typography>By using our website, you hereby consent to our Privacy Policy and agree to its terms.</Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="informationCollect">
                  Information we collect
                </Typography>
                <Typography>
                  When we ask you for personal information, the personal information you are asked to provide and the reasons you
                  are asked to provide it will be made clear to you.
                </Typography>
                <Box mt={3} />
                <Typography>
                  If you contact us directly, we may receive additional information about you, such as your name, email address,
                  phone number, the contents of the message and/or attachments you may send us, and any other information you may
                  choose to provide.
                </Typography>
                <Box mt={3} />
                <Typography>
                  When you register for a Contract, we may ask for your contact information, including name, company name,
                  address, email address, and telephone number.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="disclosureOfInformation">
                  Disclosure of Information
                </Typography>
                <Typography>
                  We do not disclose your personal information to third parties for their marketing purposes or otherwise.
                  However, we may share your information with carefully selected service providers who assist us in operating our
                  website or fulfill orders on our behalf.
                </Typography>
                <Box mt={3} />
                <Typography>
                  These service providers are contractually obligated to maintain the confidentiality and security of your
                  information in compliance with applicable data protection laws and regulations.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="smsConsentAndPhoneNumbers">
                  SMS Consent and Phone Numbers
                </Typography>
                <Typography>
                  We respect your privacy regarding SMS communication. Any consent provided for SMS notifications, reminders, or
                  updates is used strictly for communication directly related to the services we provide. We do not share your
                  phone numbers or SMS consent with third parties.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="howUseInformation">
                  How we use your information
                </Typography>
                <Typography>We use the information we collect in various ways, including:</Typography>
                <ul>
                  <li>
                    <Typography>Provide, operate, and maintain our website and our services;</Typography>
                  </li>
                  <li>
                    <Typography>Improve, personalize, and expand our website and our services;</Typography>
                  </li>
                  <li>
                    <Typography>Understand and analyze how you use our website and our services;</Typography>
                  </li>
                  <li>
                    <Typography>Develop new products, services, features, and functionality;</Typography>
                  </li>
                  <li>
                    <Typography>Create new Contracts and store the information in relevant databases;</Typography>
                  </li>
                  <li>
                    <Typography>Share information with suppliers;</Typography>
                  </li>
                  <li>
                    <Typography>Collect information for successful contract signing;</Typography>
                  </li>
                  <li>
                    <Typography>Analyze provided information to provide appropriate service options;</Typography>
                  </li>
                  <li>
                    <Typography>Collect information for review by our pricing managers;</Typography>
                  </li>
                  <li>
                    <Typography>
                      Communicate with you, either directly or through one of our partners, including for customer service, to
                      provide you with updates and other information relating to the website and our services, and for marketing
                      and promotional purposes;
                    </Typography>
                  </li>
                  <li>
                    <Typography>Send you emails;</Typography>
                  </li>
                  <li>
                    <Typography>Find and prevent fraud.</Typography>
                  </li>
                </ul>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="logFiles">
                  Log Files
                </Typography>
                <Typography>
                  Utilities One follows a standard procedure of using log files. These files log visitors when they visit
                  websites. All hosting companies do this and are a part of hosting services' analytics. The information collected
                  by log files includes internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date and
                  time stamp, referring/exit pages, and possibly the number of clicks. These are not related to any personally
                  identifiable information. The information aims to analyze trends, administer the site, track user's movement on
                  the website, and gather demographic information.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="cookies">
                  Cookies and Web Beacons
                </Typography>
                <Typography>
                  Like any other website, Utilities One uses cookies. These cookies store information, including visitors'
                  preferences and the pages on the website they accessed or visited. The data is used to optimize the users'
                  experience by customizing our web page content based on visitors' browser type and/or other information.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="ccpa">
                  CCPA Privacy Rights (Do Not Sell My Personal Information)
                </Typography>
                <Typography>Under the CCPA, among other rights, California consumers have the right to:</Typography>
                <ul>
                  <li>
                    <Typography>
                      Request that a business that collects a consumer's data disclose the categories and specific pieces of
                      personal data that a business has collected about consumers.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Request that a business that collects a consumer's data disclose the categories and specific pieces of
                      personal data that a business has collected about consumers.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Request that a business delete any personal data about the consumer that a business has collected.
                    </Typography>
                  </li>

                  <li>
                    <Typography>
                      Request that a business that sells a consumer's data not sell the consumer's data. If you make a request, we
                      have one month to respond to you.
                    </Typography>
                  </li>
                </ul>
                <Typography>If you would like to exercise any of these rights, please contact us.</Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="gdpr">
                  GDPR Data Protection Rights
                </Typography>
                <Typography>
                  We want to ensure you are fully aware of your data protection rights. Every user is entitled to the following:
                </Typography>
                <ul>
                  <li>
                    <Typography>
                      The right to access – You have the right to request copies of your data. We may charge you a small fee for
                      this service.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      The right to rectification – You have the right to request that we correct any information you believe is
                      inaccurate. You also have the right to request that we complete the information you believe is incomplete.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      The right to erasure – You have the right to request that we erase your data under certain conditions.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      The right to restrict processing – You have the right to request that we restrict your data processing under
                      certain conditions.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      The right to object to processing – You have the right to object to our processing of your data under
                      certain conditions.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      The right to data portability – You have the right to request that we transfer the data that we have
                      collected to another organization or directly to you under certain conditions.
                    </Typography>
                  </li>
                </ul>
                <Typography>
                  If you make a request, we have one month to respond. Please contact us if you would like to exercise any of
                  these rights.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="children">
                  Children's Information
                </Typography>
                <Typography>
                  Another part of our priority is adding protection for children while using the internet. We encourage parents
                  and guardians to observe, participate in, and/or monitor and guide their online activity. Utilities One does not
                  knowingly collect any Personal Identifiable Information from children under the age of 13. Suppose you think
                  your child provided this kind of information on our website. In that case, we strongly encourage you to contact
                  us immediately, and we will do our best to remove such information from our records promptly.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
export default PrivacyPolicy;
